import {
  Operator
} from '../hooks/useMultiFilter';

export type FieldType = 'text' | 'number' | 'date' | 'boolean' | 'url' | 'email';

export interface OperatorOption {
  value: Operator;
  label: string;
  requiresValue: boolean;
}

// Text field operators
export const TEXT_OPERATORS: OperatorOption[] = [
  { value: 'contains', label: 'Contains', requiresValue: true },
  { value: 'does_not_contain', label: 'Does not contain', requiresValue: true },
  { value: 'equals', label: 'Equals', requiresValue: true },
  { value: 'not_equals', label: 'Not equals', requiresValue: true },
  { value: 'starts_with', label: 'Starts with', requiresValue: true },
  { value: 'ends_with', label: 'Ends with', requiresValue: true },
  { value: 'is_empty', label: 'Is empty', requiresValue: false },
  { value: 'is_not_empty', label: 'Is not empty', requiresValue: false },
  { value: 'is_one_of', label: 'Is one of', requiresValue: true },
  { value: 'is_not_one_of', label: 'Is not one of', requiresValue: true }
];

// Number field operators
export const NUMBER_OPERATORS: OperatorOption[] = [
  { value: 'eq', label: '=', requiresValue: true },
  { value: 'neq', label: '≠', requiresValue: true },
  { value: 'gt', label: '>', requiresValue: true },
  { value: 'gte', label: '≥', requiresValue: true },
  { value: 'lt', label: '<', requiresValue: true },
  { value: 'lte', label: '≤', requiresValue: true },
  { value: 'between', label: 'Between', requiresValue: true },
  { value: 'is_empty', label: 'Is empty', requiresValue: false },
  { value: 'is_not_empty', label: 'Is not empty', requiresValue: false }
];

// Date field operators
export const DATE_OPERATORS: OperatorOption[] = [
  { value: 'on', label: 'On', requiresValue: true },
  { value: 'before', label: 'Before', requiresValue: true },
  { value: 'after', label: 'After', requiresValue: true },
  { value: 'between', label: 'Between', requiresValue: true },
  { value: 'in_the_last', label: 'In the last', requiresValue: true },
  { value: 'not_in_the_last', label: 'Not in the last', requiresValue: true },
  { value: 'is_empty', label: 'Is empty', requiresValue: false },
  { value: 'is_not_empty', label: 'Is not empty', requiresValue: false }
];

// Boolean field operators
export const BOOLEAN_OPERATORS: OperatorOption[] = [
  { value: 'is_true', label: 'Is true', requiresValue: false },
  { value: 'is_false', label: 'Is false', requiresValue: false },
  { value: 'is_empty', label: 'Is empty', requiresValue: false },
  { value: 'is_not_empty', label: 'Is not empty', requiresValue: false }
];

// Get operators for a field type
export const getOperatorsForFieldType = (fieldType: FieldType): OperatorOption[] => {
  switch (fieldType) {
    case 'number':
      return NUMBER_OPERATORS;
    case 'date':
      return DATE_OPERATORS;
    case 'boolean':
      return BOOLEAN_OPERATORS;
    case 'text':
    case 'url':
    case 'email':
    default:
      return TEXT_OPERATORS;
  }
};

// Get default operator for a field type
export const getDefaultOperator = (fieldType: FieldType): Operator => {
  switch (fieldType) {
    case 'number':
      return 'eq';
    case 'date':
      return 'on';
    case 'boolean':
      return 'is_true';
    case 'text':
    case 'url':
    case 'email':
    default:
      return 'contains';
  }
};

// Determine field type from column metadata
export const inferFieldType = (columnName: string, columnType?: string): FieldType => {
  const name = columnName.toLowerCase();

  // Check explicit type first
  if (columnType === 'number' || columnType === 'integer' || columnType === 'float') {
    return 'number';
  }
  if (columnType === 'date' || columnType === 'datetime' || columnType === 'timestamp') {
    return 'date';
  }
  if (columnType === 'boolean' || columnType === 'bool') {
    return 'boolean';
  }

  // Infer from name
  if (name.includes('url') || name.includes('link') || name.includes('website')) {
    return 'url';
  }
  if (name.includes('email') || name.includes('mail')) {
    return 'email';
  }
  if (name.includes('date') || name.includes('time') || name.includes('created') || name.includes('updated')) {
    return 'date';
  }
  if (name.includes('count') || name.includes('number') || name.includes('amount') || name.includes('price') || name.includes('revenue')) {
    return 'number';
  }
  if (name.includes('is_') || name.includes('has_') || name.includes('enabled') || name.includes('active')) {
    return 'boolean';
  }

  return 'text';
};

// Check if an operator requires a value input
export const operatorRequiresValue = (operator: Operator): boolean => {
  const allOperators = [
    ...TEXT_OPERATORS,
    ...NUMBER_OPERATORS,
    ...DATE_OPERATORS,
    ...BOOLEAN_OPERATORS
  ];

  const operatorConfig = allOperators.find(op => op.value === operator);
  return operatorConfig?.requiresValue ?? true;
};

// Evaluate a single condition against a row value
export const evaluateCondition = (
  rowValue: any,
  operator: Operator,
  filterValue: any
): boolean => {
  // Handle empty/null checks
  const isEmpty = rowValue === null || rowValue === undefined || rowValue === '';

  if (operator === 'is_empty') return isEmpty;
  if (operator === 'is_not_empty') return !isEmpty;

  // If row value is empty and operator requires a value, condition fails
  if (isEmpty) return false;

  // Convert to strings for text operations
  const rowStr = String(rowValue).toLowerCase();
  const filterStr = filterValue ? String(filterValue).toLowerCase() : '';

  // Text operators
  if (operator === 'contains') return rowStr.includes(filterStr);
  if (operator === 'does_not_contain') return !rowStr.includes(filterStr);
  if (operator === 'equals') return rowStr === filterStr;
  if (operator === 'not_equals') return rowStr !== filterStr;
  if (operator === 'starts_with') return rowStr.startsWith(filterStr);
  if (operator === 'ends_with') return rowStr.endsWith(filterStr);

  if (operator === 'is_one_of') {
    if (!Array.isArray(filterValue)) return false;
    return filterValue.some(val => String(val).toLowerCase() === rowStr);
  }
  if (operator === 'is_not_one_of') {
    if (!Array.isArray(filterValue)) return true;
    return !filterValue.some(val => String(val).toLowerCase() === rowStr);
  }

  // Number operators
  const rowNum = Number(rowValue);
  const filterNum = Number(filterValue);

  if (operator === 'eq') return rowNum === filterNum;
  if (operator === 'neq') return rowNum !== filterNum;
  if (operator === 'gt') return rowNum > filterNum;
  if (operator === 'gte') return rowNum >= filterNum;
  if (operator === 'lt') return rowNum < filterNum;
  if (operator === 'lte') return rowNum <= filterNum;

  if (operator === 'between') {
    if (!Array.isArray(filterValue) || filterValue.length !== 2) return false;
    const [min, max] = filterValue.map(Number);
    return rowNum >= min && rowNum <= max;
  }

  // Date operators
  const rowDate = new Date(rowValue);
  const filterDate = filterValue ? new Date(filterValue) : null;

  if (operator === 'on') {
    if (!filterDate) return false;
    return rowDate.toDateString() === filterDate.toDateString();
  }
  if (operator === 'before') {
    if (!filterDate) return false;
    return rowDate < filterDate;
  }
  if (operator === 'after') {
    if (!filterDate) return false;
    return rowDate > filterDate;
  }

  // Boolean operators
  if (operator === 'is_true') return rowValue === true || rowValue === 'true' || rowValue === 1;
  if (operator === 'is_false') return rowValue === false || rowValue === 'false' || rowValue === 0;

  return false;
};
